import { ApiHandler } from '../api-handler';
import { ApiEndpoints, HttpMethod } from '../constants';
import { GeneralApiResponse } from '../types';
import { buildQueryParams } from '../utils';
import {
  VerifyInviteTokenResponse,
  DoctorsApiResponse,
  GetDoctorsParams,
  CreateDoctorAdminReqBody,
  CreateDoctorAdminResBody,
  CreateDoctorsBulkBody,
  ImportDoctorsCsvPayload,
  ImportDoctorsCsvResponse,
  UpdateDoctorProfileAdminReqBody,
  DoctorProfileUpdatePayload,
} from '../user/types';

export const DoctorApiService = {
  getDoctors: (params?: GetDoctorsParams) => {
    const { page = 1, limit = 10, ...restParams } = params ?? {};
    const query: Record<string, string> = buildQueryParams({
      page,
      limit,
      ...restParams,
    });

    return ApiHandler.makeApiRequest<DoctorsApiResponse>({
      endpoint: ApiEndpoints.Doctors.Base,
      method: HttpMethod.Get,
      query,
      rejectOnError: true,
    });
  },

  createDoctorAdmin: (data: CreateDoctorAdminReqBody) =>
    ApiHandler.makeApiRequest<CreateDoctorAdminResBody, CreateDoctorAdminReqBody>({
      endpoint: ApiEndpoints.Doctors.Base,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  createDoctorsBulk: (data: CreateDoctorsBulkBody) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, CreateDoctorsBulkBody>({
      endpoint: ApiEndpoints.Doctors.Bulk,
      method: HttpMethod.Post,
      rejectOnError: true,
      data,
    }),

  importDoctorsCsv: (payload: ImportDoctorsCsvPayload) =>
    ApiHandler.makeApiRequest<ImportDoctorsCsvResponse, FormData>({
      endpoint: ApiEndpoints.Doctors.BulkUpload,
      method: HttpMethod.Post,
      data: payload,
      rejectOnError: true,
    }),

  updateDoctorActive: (doctorId: string, isActive: boolean) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, { isActive: boolean }>({
      endpoint: ApiEndpoints.Doctors.Active(doctorId),
      method: HttpMethod.Patch,
      data: { isActive },
      rejectOnError: true,
    }),

  updateDoctorProfileAdmin: (doctorId: string, payload: UpdateDoctorProfileAdminReqBody) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, UpdateDoctorProfileAdminReqBody>({
      endpoint: ApiEndpoints.Doctors.Profile(doctorId),
      method: HttpMethod.Patch,
      data: payload,
      rejectOnError: true,
    }),

  resendInvite: (doctorId: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Doctors.InvitesResend(doctorId),
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  verifyToken: (token: string, rejectOnError?: boolean) =>
    ApiHandler.makeApiRequest<VerifyInviteTokenResponse, { token: string }>({
      endpoint: ApiEndpoints.Doctors.InvitesVerify,
      method: HttpMethod.Post,
      withAuth: false,
      rejectOnError,
      data: { token },
    }),

  acceptInvite: (token: string, firebaseIdToken: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, { token: string; firebaseIdToken: string }>({
      endpoint: ApiEndpoints.Doctors.InvitesAccept,
      method: HttpMethod.Post,
      withAuth: false,
      rejectOnError: true,
      data: { token, firebaseIdToken },
    }),

  updateDoctorProfile: (payload: DoctorProfileUpdatePayload, rejectOnError = false) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, DoctorProfileUpdatePayload>({
      endpoint: ApiEndpoints.Doctors.SelfProfile,
      method: HttpMethod.Put,
      data: payload,
      rejectOnError,
    }),
} as const;
